import { useState } from 'react'
import { can, saveFile } from '../../lib/api'
import { exitApi, type ExitChain, type ExitSummary } from '../../lib/exit'
import { personLabel, usePeopleNames } from '../../lib/people'
import { useSave } from '../../lib/save'
import { ErrorBox } from '../DataTable'
import { Dialog, Status, Toggletip } from '../kit'
import { DateRuleNote } from '../DateRuleNote'
import { Crumbs } from '../Crumbs'
import { checkOffboardingDate, todayIso, useLeftFields } from '../../lib/dateRules'
import { TRIGGER_WORDS, dayMonth, dayMonthYear, exitWord, openFor, shortPerson } from './words'

// The top of the exit workspace: which exit this is, the one status it has and
// the facts that chose its chain.
//
// # WHY THE FACTS ARE CHIPS
//
// The tier, whether personal data was held and whether an alternate provider
// exists are what decide how many offices sign and which. They sit beside the
// status so a reader who asks "why does the CEO sign this one" has the answer
// in the header, and the whole reasoning is in About this chain.

export function ExitHeader({
  vendorId,
  vendorName,
  vendorCode,
  status,
  chain,
  summary,
  onChanged,
}: {
  vendorId: string
  vendorName: string
  vendorCode: string
  status: string
  chain: ExitChain
  summary: ExitSummary | null
  onChanged: () => void
}) {
  const names = usePeopleNames()
  const label = (s: string | undefined) => personLabel(names, s)
  const [proposing, setProposing] = useState(false)
  const exit = chain.exit
  const proposal = exit?.status === 'PROPOSED' ? exit : null
  const underWay = status === 'OFFBOARDING'
  const signedOut = status === 'OFFBOARDED'
  const inRegister = status === 'ACTIVE' || status === 'SUSPENDED'
  const mayRun = can('offboarding.run')
  const word = exitWord(summary, status, !!proposal, chain.met, mayRun)
  const facts = [
    chain.facts.personalData ? 'Personal data' : 'No personal data',
    chain.facts.noAlternate ? 'no alternate provider' : 'alternate provider named',
  ].join(', ')
  const day = underWay ? openFor(summary?.daysOpen, summary?.plannedDays) : ''

  return (
    <header className="exit-ws-head">
      <Crumbs className="exit-crumb" home={{ label: 'Offboarding', href: '#/exits' }} current={vendorName} />
      <div className="exit-head">
        <div className="exit-head-grow">
          <h1>{vendorName} offboarding</h1>
          <div className="exit-meta">
            <Status tone={word.tone} title={summary?.blocker}>
              {word.text}
            </Status>
            {chain.facts.tierLabel && <span className="exit-tag">{chain.facts.tierLabel}</span>}
            <span className="kit-mono">{vendorCode}</span>
            <span>{facts}</span>
            {day && <span className="exit-mono">{day}</span>}
            {exit && !proposal && exit.triggerKind && <span>{TRIGGER_WORDS[exit.triggerKind] ?? exit.triggerKind}</span>}
            {exit?.openedBy && !proposal && (
              <span className="exit-inline">
                Started by {shortPerson(label(exit.openedBy))}
                {exit.openedAt ? ', ' + dayMonth(exit.openedAt) : ''}
                <Toggletip label="Who may sign">Whoever started offboarding may sign no stage of it.</Toggletip>
              </span>
            )}
            {signedOut && exit?.closedAt && <span className="exit-mono">Closed {dayMonthYear(exit.closedAt)}</span>}
          </div>
        </div>
        <div className="exit-actions">
          <a className="button" href={'#/vendor/' + vendorId}>
            Vendor record
          </a>
          {inRegister && !proposal && mayRun && (
            <button type="button" className="primary" onClick={() => setProposing(true)}>
              Propose offboarding
            </button>
          )}
          {signedOut && can('audit.read') && <DossierButtons vendorId={vendorId} />}
        </div>
      </div>

      <ProposeDrawer
        vendorId={vendorId}
        open={proposing}
        onClose={() => setProposing(false)}
        onDone={() => {
          setProposing(false)
          onChanged()
        }}
      />
    </header>
  )
}

// ------------------------------------------------------------ proposing

function ProposeDrawer({ vendorId, open, onClose, onDone }: { vendorId: string; open: boolean; onClose: () => void; onDone: () => void }) {
  const [kind, setKind] = useState<'REGULATOR_INSTRUCTION' | 'MANUAL'>('MANUAL')
  const [regulator, setRegulator] = useState('')
  const [reference, setReference] = useState('')
  const [receivedOn, setReceivedOn] = useState('')
  const [reason, setReason] = useState('')
  const save = useSave(open, 'Propose offboarding')
  const busy = save.busy
  const [err, setErr] = useState<unknown>(null)
  const regulatorKind = kind === 'REGULATOR_INSTRUCTION'
  // D22 of the date rules: the letter has arrived, so it is not dated in the
  // future. It comes before the exit by its nature, so no start applies.
  const left = useLeftFields()
  const receivedRule = regulatorKind ? checkOffboardingDate('Letter received on', receivedOn, '', todayIso()) : ''

  function submit() {
    if (receivedRule) {
      left.leave('receivedOn')
      return
    }
    setErr(null)
    const body = regulatorKind ? { triggerKind: kind, reason, regulator, reference, receivedOn } : { triggerKind: kind, reason }
    save.send(exitApi.propose(vendorId, body), {
      done: () => {
        setReason('')
        setRegulator('')
        setReference('')
        setReceivedOn('')
        onDone()
      },
      failed: setErr,
    })
  }


  return (
    <Dialog
      shape="modal"
      kind="form"
      open={open}
      onClose={onClose}
      title="Propose offboarding"
      busy={busy}
      primary={{
        label: busy ? 'Recording' : 'Propose offboarding',
        off: reason.trim() === '' && (regulatorKind ? 'Say what the regulator directed' : 'Say why this vendor should leave'),
        onClick: submit,
        actionId: 'tprm.exit-workspace.a03',
      }}
    >
      <div className="exit-drawer-stack">
        <p className="exit-sub">Nothing changes until somebody else starts it</p>
        {err != null && <ErrorBox error={err} what="The proposal" verb="recorded" />}
        <fieldset className="fieldset">
          <legend>What is behind it</legend>
          <div className="check">
            <input id="prop-kind-manual" type="radio" name="prop-kind" checked={!regulatorKind} onChange={() => setKind('MANUAL')} />
            <label htmlFor="prop-kind-manual">Our own decision</label>
          </div>
          <div className="check">
            <input
              id="prop-kind-regulator"
              type="radio"
              name="prop-kind"
              checked={regulatorKind}
              onChange={() => setKind('REGULATOR_INSTRUCTION')}
            />
            <label htmlFor="prop-kind-regulator">A regulator's instruction</label>
          </div>
        </fieldset>
        {regulatorKind && (
          <>
            <div className="field">
              <label htmlFor="prop-regulator">Regulator</label>
              <input id="prop-regulator" type="text" value={regulator} onChange={(e) => setRegulator(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="prop-reference">Reference on the letter</label>
              <input id="prop-reference" type="text" value={reference} onChange={(e) => setReference(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="prop-received">Letter received on</label>
              <input
                id="prop-received"
                type="date"
                value={receivedOn}
                onChange={(e) => setReceivedOn(e.target.value)}
                onBlur={() => left.leave('receivedOn')}
                aria-invalid={left.shown('receivedOn', receivedRule) ? true : undefined}
                aria-describedby={left.shown('receivedOn', receivedRule) ? 'prop-received-rule' : undefined}
              />
              <DateRuleNote id="prop-received-rule" message={left.shown('receivedOn', receivedRule)} />
            </div>
          </>
        )}
        <div className="field">
          <label htmlFor="prop-reason">{regulatorKind ? 'What the regulator directed' : 'Why this vendor should leave'}</label>
          <textarea id="prop-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
      </div>
    </Dialog>
  )
}

// ------------------------------------------------------------ the dossier

function DossierButtons({ vendorId }: { vendorId: string }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<unknown>(null)

  function get(format: 'json' | 'html') {
    setBusy(true)
    setErr(null)
    exitApi
      .dossierFile(vendorId, format)
      .then(saveFile)
      .catch(setErr)
      .finally(() => setBusy(false))
  }

  return (
    <>
      <button
        type="button"
        data-action="tprm.exit-workspace.a04"
        disabled={busy}
        onClick={() => get('html')}
        title="One pack of every approval and step, with its evidence"
      >
        Dossier to read
      </button>
      <button type="button" data-action="tprm.exit-workspace.a04" disabled={busy} onClick={() => get('json')}>
        Dossier as data
      </button>
      {err != null && <ErrorBox error={err} what="The offboarding dossier" verb="produced" />}
    </>
  )
}
